import { useState, useEffect, useCallback } from 'react';

// Feature flag names
export enum FeatureFlag {
  ENABLE_BUNDLE_ADD_ONS = 'ENABLE_BUNDLE_ADD_ONS',
}

// Default values for feature flags
const DEFAULT_FLAGS: Record<FeatureFlag, boolean> = {
  [FeatureFlag.ENABLE_BUNDLE_ADD_ONS]: true,
};

// Get environment variables from Vite
const ENV_FLAGS: Partial<Record<FeatureFlag, boolean>> = {
  [FeatureFlag.ENABLE_BUNDLE_ADD_ONS]: import.meta.env.VITE_FEATURE_FLAG_ENABLE_BUNDLE_ADD_ONS === 'true',
};

// Helper function to check if a flag is controlled by an environment variable
export const isFeatureFlagControlledByEnv = (flagName: FeatureFlag): boolean => ENV_FLAGS[flagName] !== undefined;

// Admin-only function to toggle a feature flag
export const toggleFeatureFlag = (flagName: FeatureFlag, value: boolean): void => {
  try {
    const flags = JSON.parse(localStorage.getItem('FEATURE_FLAGS') || '{}');
    flags[flagName] = value;
    localStorage.setItem('FEATURE_FLAGS', JSON.stringify(flags));
  } catch (error) {
    // If JSON parsing fails, create a new flags object
    const flags = { [flagName]: value };
    localStorage.setItem('FEATURE_FLAGS', JSON.stringify(flags));
  }
  // Dispatch an event so other components can react to the change
  window.dispatchEvent(new Event('featureFlagChanged'));
};

// Hook to access feature flags
export const useFeatureFlag = (flagName: FeatureFlag): boolean => {
  // Function to get the current flag value
  const getFlagValue = useCallback(() => {
    try {
      // First check environment variables (pipeline variables)
      if (ENV_FLAGS[flagName] !== undefined) {
        return ENV_FLAGS[flagName];
      }
      // Then check localStorage for overrides (only if no environment variable is set)
      const flags = JSON.parse(localStorage.getItem('FEATURE_FLAGS') || '{}');
      if (flags[flagName] !== undefined) {
        return flags[flagName];
      }
      // Finally fall back to defaults
      return DEFAULT_FLAGS[flagName] ?? false;
    } catch (error) {
      // If JSON parsing fails or any other error occurs, fall back to default
      return DEFAULT_FLAGS[flagName] ?? false;
    }
  }, [flagName]);

  // Use state to track the flag value
  const [flagValue, setFlagValue] = useState<boolean>(getFlagValue());

  // This effect runs when the component mounts and when the flagName changes
  useEffect(() => {
    // Update the state with the current value from localStorage
    setFlagValue(getFlagValue());

    // Set up an event listener to update the state when the flag changes
    const handleFlagChange = () => {
      setFlagValue(getFlagValue());
    };

    // Listen for the custom event
    window.addEventListener('featureFlagChanged', handleFlagChange);
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('featureFlagChanged', handleFlagChange);
    };
  }, [flagName, getFlagValue]);

  // For testing purposes, we'll also check localStorage on each render
  // This ensures the hook always returns the latest value
  const currentValue = getFlagValue();
  if (currentValue !== flagValue) {
    // If the value in localStorage doesn't match our state, update the state
    setFlagValue(currentValue);
  }

  return flagValue;
};

// Specific hook for the bundle add-ons feature (franchisee only)
export const useBundleAddOnsFeatureFlag = (): boolean => useFeatureFlag(FeatureFlag.ENABLE_BUNDLE_ADD_ONS);
